import {
  AppCard,
  Icon,
  PageContainer,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { mdiDownload, mdiEye, mdiTruck } from "@mdi/js";
import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useShowQuotationPurchaseOrderDetails } from "../../../../../__generated__/orval/endpoints/purchase-orders/purchase-orders";
import { EntitiesPurchaseOrderQuotationMemberMain } from "../../../../../__generated__/orval/models/entitiesPurchaseOrderQuotationMemberMain.zod";
import { ForbiddenError } from "../../../../../errors/ForbiddenError";
import { useAuth } from "../../../../../hooks/useAuth";
import { i18nextParseToDate } from "../../../../../utils/i18nextParseTo";
import { PurchaseOrderDownloadDialog } from "../-internal_components/PurchaseOrderDownloadDialog";

const PurchaseOrderDetails: React.FC = () => {
  const { purchaseOrderId } = Route.useParams();
  const { user } = useAuth();
  const { t } = useTranslation();
  const userVendor = Boolean(user?.vendor);
  const purchaseOrderIdNumber = Number(purchaseOrderId);
  const {
    data: purchaseOrder,
    isError,
    isLoading,
  } = useShowQuotationPurchaseOrderDetails(purchaseOrderIdNumber, {
    query: {
      enabled:
        Number.isInteger(purchaseOrderIdNumber) && purchaseOrderIdNumber > 0,
      select: (response) => {
        const result =
          EntitiesPurchaseOrderQuotationMemberMain.safeParse(response);

        if (!result.success) {
          throw new Error(result.error.message);
        }

        return result.data;
      },
    },
  });

  return (
    <PageContainer
      maxWidth={false}
      showBackButton
      title={t("quotationPurchaseOrders.detailsTitle")}
    >
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
          <CircularProgress aria-label={t("common.loading")} />
        </Box>
      ) : isError || !purchaseOrder ? (
        <Typography color="error" role="alert">
          {t("quotationPurchaseOrders.detailsFetchError")}
        </Typography>
      ) : (
        <Stack spacing={2}>
          <AppCard>
            {purchaseOrder.quotations.length > 0 ? (
              <TableContainer sx={{ overflowX: "auto" }}>
                <Table
                  aria-label={t("quotationPurchaseOrders.detailsTitle")}
                  size="small"
                  sx={{ minWidth: 1150 }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        {t("quotationPurchaseOrders.purchaseOrderNumber")}
                      </TableCell>
                      <TableCell>
                        {t("quotationPurchaseOrders.requisitionNumber")}
                      </TableCell>
                      <TableCell>
                        {t("quotationPurchaseOrders.status")}
                      </TableCell>
                      <TableCell>
                        {t("servicePurchaseOrders.criticality")}
                      </TableCell>
                      <TableCell>{t("servicePurchaseOrders.plant")}</TableCell>
                      <TableCell>{t("common.vendor")}</TableCell>
                      <TableCell>{t("common.date")}</TableCell>
                      <TableCell align="center">
                        {t("common.actions")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {purchaseOrder.quotations.map((quotation) => (
                      <TableRow hover key={quotation.id}>
                        <TableCell>{`#${purchaseOrder.display_number}`}</TableCell>
                        <TableCell>{quotation.identifier}</TableCell>
                        <TableCell>{quotation.status || "-"}</TableCell>
                        <TableCell>{quotation.criticality || "-"}</TableCell>
                        <TableCell>{quotation.plant_name || "-"}</TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {purchaseOrder.vendor_name}
                          </Typography>
                          <Typography color="text.secondary" variant="body2">
                            {quotation.vendor_email || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {t(
                            "intlDate",
                            i18nextParseToDate(purchaseOrder.sap_created_at),
                          )}
                        </TableCell>
                        <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                          <Box
                            sx={{
                              alignItems: "center",
                              display: "flex",
                              flexWrap: "nowrap",
                              gap: 1,
                              justifyContent: "center",
                            }}
                          >
                            {purchaseOrder.purchase_order_pdf_filename ? (
                              userVendor ? (
                                <PurchaseOrderDownloadDialog
                                  purchaseOrder={purchaseOrder}
                                />
                              ) : (
                                <Tooltip title={t("common.download")}>
                                  <IconButton
                                    aria-label={t("common.download")}
                                    component="a"
                                    download={
                                      purchaseOrder.purchase_order_pdf_filename
                                    }
                                    href={`/vbuyer/purchase_orders/${purchaseOrder.id}/download.pdf`}
                                    size="small"
                                    target="_blank"
                                  >
                                    <Icon icon={mdiDownload} />
                                  </IconButton>
                                </Tooltip>
                              )
                            ) : (
                              <Tooltip title={t("attachments.noAttachments")}>
                                <span>
                                  <IconButton
                                    aria-label={t("common.download")}
                                    disabled
                                    size="small"
                                  >
                                    <Icon icon={mdiDownload} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            {!userVendor || quotation.negotiation_id ? (
                              <Tooltip
                                title={t("quotationPurchaseOrders.moreDetails")}
                              >
                                <IconButton
                                  aria-label={t(
                                    "quotationPurchaseOrders.moreDetails",
                                  )}
                                  component="a"
                                  href={
                                    userVendor
                                      ? `/vbuyer/negotiations/${quotation.negotiation_id}`
                                      : `/vbuyer/quotations/${quotation.id}`
                                  }
                                  size="small"
                                >
                                  <Icon icon={mdiEye} />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              "-"
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ alignItems: "center", display: "flex", gap: 1 }}>
                <Typography color="text.secondary">
                  {t("quotationPurchaseOrders.emptyQuotations")}
                </Typography>
                {purchaseOrder.purchase_order_pdf_filename ? (
                  userVendor ? (
                    <PurchaseOrderDownloadDialog
                      purchaseOrder={purchaseOrder}
                    />
                  ) : (
                    <Tooltip title={t("common.download")}>
                      <IconButton
                        aria-label={t("common.download")}
                        component="a"
                        download={purchaseOrder.purchase_order_pdf_filename}
                        href={`/vbuyer/purchase_orders/${purchaseOrder.id}/download.pdf`}
                        size="small"
                        target="_blank"
                      >
                        <Icon icon={mdiDownload} />
                      </IconButton>
                    </Tooltip>
                  )
                ) : null}
              </Box>
            )}
          </AppCard>

          {purchaseOrder.invoices.length > 0 && (
            <AppCard>
              <Typography component="h2" sx={{ mb: 2 }} variant="h6">
                {t("quotationPurchaseOrders.goodsReceipt")}
              </Typography>
              <TableContainer sx={{ overflowX: "auto" }}>
                <Table
                  aria-label={t("quotationPurchaseOrders.goodsReceipt")}
                  size="small"
                  sx={{ minWidth: 850, textAlign: "center" }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        {t("quotationPurchaseOrders.invoice")}
                      </TableCell>
                      <TableCell>{t("common.createdAt")}</TableCell>
                      <TableCell>
                        {t("quotationPurchaseOrders.overallStatus")}
                      </TableCell>
                      <TableCell>
                        {t("quotationPurchaseOrders.steps")}
                      </TableCell>
                      <TableCell>
                        {t("quotationPurchaseOrders.issues")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {purchaseOrder.invoices.map((invoice) => (
                      <TableRow hover key={invoice.id}>
                        <TableCell>
                          {`${invoice.invoice_number}-${invoice.invoice_series}`}
                        </TableCell>
                        <TableCell>
                          {t(
                            "intlDate",
                            i18nextParseToDate(invoice.created_at),
                          )}
                        </TableCell>
                        <TableCell>{invoice.overall_status || "-"}</TableCell>
                        <TableCell>
                          {t("quotationPurchaseOrders.outOf", {
                            completed: invoice.completed_steps,
                            total: invoice.total_steps,
                          })}
                        </TableCell>
                        <TableCell>
                          {t("quotationPurchaseOrders.outOf", {
                            completed: invoice.resolved_issues,
                            total: invoice.total_issues,
                          })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box
                sx={{
                  color: "success.main",
                  display: "flex",
                  justifyContent: "center",
                  mt: 2,
                }}
              >
                <Icon icon={mdiTruck} />
              </Box>
            </AppCard>
          )}
        </Stack>
      )}
    </PageContainer>
  );
};

export const Route = createFileRoute(
  "/_private/purchase_orders/materials/$purchaseOrderId/",
)({
  beforeLoad({ context }) {
    if (context.auth.user?.roles_names.includes("Purchase Order - View")) {
      return;
    }

    throw new ForbiddenError();
  },
  component: PurchaseOrderDetails,
});
